import { FC } from "react";
import { FileText } from "lucide-react";
import {
  DebitPermissionDto,
  GetRechnungDetailsDto,
  PatientBillInfoDto,
} from "../../../../api-client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import RxPriceTableHeader from "./RxPriceTableHeader";
import RxPriceTableRow from "./RxPriceTableRow";

interface RxPriceTableProps {
  billsInfo: PatientBillInfoDto[];
  rechnungMap: Record<string, GetRechnungDetailsDto[]>;
  onClickPay: (bill: PatientBillInfoDto) => void;
  onClickPaybackToPatient: (bill: PatientBillInfoDto) => void;
  onClickBill: (bill: PatientBillInfoDto) => void;
  onClickLastschrift: (bill: PatientBillInfoDto, permission: DebitPermissionDto) => void;
  /** Opens the one-off (Einmal Lastschrift) wizard directly to resume an in-progress mandate. */
  onResumeEinmal: (bill: PatientBillInfoDto) => void;
  onHandleSepaButtonClick: (
    bill: PatientBillInfoDto,
    validSepaPermissions: DebitPermissionDto[],
  ) => void;
  onHandleSepaSelect: (bill: PatientBillInfoDto, permission: DebitPermissionDto) => void;
  handleExternalBill: (bill: PatientBillInfoDto) => void;
  isRowBusy: (rxId?: string) => boolean;
  debitPermissions: DebitPermissionDto[];
  sepaMenuState: {
    anchorEl: HTMLElement | null;
    openMenus: { [key: string]: boolean };
  };
  onCloseSepaMenu: (rxId?: string) => void;
  withLetterhead: boolean;
  onToggleLetterhead: (checked: boolean) => void;
}

/**
 * Drop-in replacement for `NewPatientBillTable` using the SEPA card design
 * (shadcn Card + table, centered uppercase headers, zebra rows). Functionality,
 * data and props are identical to the original; the letterhead switch moves into
 * the card header. Rows open `PatientBillDetailsModalShad` and reuse
 * `PatientBillActions` unchanged.
 */
const RxPriceTable: FC<RxPriceTableProps> = ({
  billsInfo,
  rechnungMap,
  onClickPay,
  onClickPaybackToPatient,
  onClickBill,
  onResumeEinmal,
  onHandleSepaButtonClick,
  onHandleSepaSelect,
  handleExternalBill,
  isRowBusy,
  debitPermissions,
  sepaMenuState,
  onCloseSepaMenu,
  withLetterhead,
  onToggleLetterhead,
}) => {
  return (
    <Card className="mt-3 overflow-hidden rounded-lg border border-solid border-black shadow-none">
      <CardHeader className="space-y-1 bg-slate-50/60 py-4">
        <CardTitle className="flex items-center gap-2 text-xl font-semibold text-slate-800">
          <FileText className="h-5 w-5 text-slate-500" />
          Rezepte
        </CardTitle>
        <div className="flex items-center justify-between gap-4">
          <p className="my-0 text-left text-sm text-slate-500">
            Offene und abgerechnete Rezepte des Patienten.
          </p>
          <div className="flex shrink-0 items-center space-x-2">
            <Switch
              id="letterhead-switch-v2"
              checked={withLetterhead}
              onCheckedChange={onToggleLetterhead}
              className="data-[state=checked]:bg-blue-dark"
            />
            <Label htmlFor="letterhead-switch-v2">Mit Briefkopf</Label>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-2">
        <div className="overflow-hidden rounded border border-slate-200">
          <Table>
            <RxPriceTableHeader />
            <TableBody>
              {billsInfo.map((bill, index) => (
                <RxPriceTableRow
                  key={index}
                  bill={bill}
                  index={index}
                  rechnungMap={rechnungMap}
                  debitPermissions={debitPermissions}
                  sepaMenuState={sepaMenuState}
                  isRowBusy={isRowBusy}
                  onClickPay={onClickPay}
                  onClickBill={onClickBill}
                  onClickPaybackToPatient={onClickPaybackToPatient}
                  onResumeEinmal={onResumeEinmal}
                  onHandleSepaButtonClick={onHandleSepaButtonClick}
                  onHandleSepaSelect={onHandleSepaSelect}
                  onCloseSepaMenu={onCloseSepaMenu}
                  handleExternalBill={handleExternalBill}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default RxPriceTable;
